import { CompleteKundali } from '../types/kundali';

export interface VastuZone {
  id: string;
  name: string;
  nameHi: string;
  angle: number; // Center degrees on 360 compass
  range: [number, number]; // [minDeg, maxDeg]
  element: 'Water' | 'Air' | 'Fire' | 'Earth' | 'Space' | 'Fire / Earth' | 'Air / Space' | 'Water / Air' | string;
  elementHi: string;
  color: string;
  deity: string;
  deityHi: string;
  rulingPlanet: string;
  rulingPlanetHi: string;
  significance: string;
  significanceHi: string;
  favorableFor: string[];
  favorableForHi: string[];
  unfavorableFor: string[];
  unfavorableForHi: string[];
  idealItems: string[];
  remedies: string;
  remediesHi: string;
}

export interface SignificantPlacement {
  id: string;
  title: string;
  titleHi: string;
  category: 'Spiritual' | 'Wealth' | 'Living' | 'Utilities' | 'Structure';
  icon: string;
  bestZones: string[];
  secondaryZones: string[];
  forbiddenZones: string[];
  deityOrLord: string;
  orientationGuidelines: string;
  orientationGuidelinesHi: string;
  vastuRules: string[];
  vastuRulesHi: string[];
  doshaEffects: string;
  doshaEffectsHi: string;
  remediesWithoutDemolition: string[];
  remediesWithoutDemolitionHi: string[];
}

export interface AstroVastuCorrelation {
  houseNumber: number;
  houseName: string;
  houseNameHi: string;
  direction: string;
  directionHi: string;
  rulingPlanet: string;
  rulingPlanetHi: string;
  significance: string;
  significanceHi: string;
  kundaliObservation: string;
  vastuAdvice: string;
  status: 'excellent' | 'balanced' | 'attention_needed';
}

// 16 Vastu Zones according to classical MahaVastu & Brihat Samhita
export const VASTU_16_ZONES: VastuZone[] = [
  {
    id: 'N',
    name: 'North (Uttar)',
    nameHi: 'उत्तर (कुबेर स्थान)',
    angle: 0,
    range: [348.75, 11.25],
    element: 'Water',
    elementHi: 'जल तत्व',
    color: '#0284C7', // Sky Blue
    deity: 'Lord Kubera & Soma',
    deityHi: 'भगवान कुबेर एवं सोम',
    rulingPlanet: 'Mercury (Budha)',
    rulingPlanetHi: 'बुध ग्रह',
    significance: 'Wealth, new career opportunities, business growth, liquidity and financial inflow.',
    significanceHi: 'धन-सम्पदा, नए व्यावसायिक अवसर, धन का आगमन व तरल नकदी।',
    favorableFor: ['Tijori / Cash Locker', 'Main Entrance (N3, N4)', 'Living Room', 'Water Fountain', 'Home Office'],
    favorableForHi: ['तिजोरी व धन संचय', 'मुख्य द्वार (N3, N4)', 'बैठक कक्ष', 'जल फव्वारा', 'कार्यस्थल / डेस्क'],
    unfavorableFor: ['Kitchen (Fire)', 'Toilet', 'Heavy Storage / Junkyard', 'Red/Orange colors'],
    unfavorableForHi: ['रसोई (अग्नि)', 'शौचालय', 'भारी कबाड़ या स्टोर', 'लाल/नारंगी रंग'],
    idealItems: ['Green plant in blue vase', 'Kubera Yantra', 'Water element art', 'Mirror'],
    remedies: 'Place a Brass Kubera Idol, water fountain, or Green Money Plant in a blue pot. Avoid red or triangular decor.',
    remediesHi: 'पीतल के कुबेर यंत्र, नीले गमले में मनी प्लांट अथवा जल फव्वारा स्थापित करें। लाल रंग से बचें।'
  },
  {
    id: 'NNE',
    name: 'North-North-East',
    nameHi: 'उत्तर-उत्तर-पूर्व (स्वास्थ्य क्षेत्र)',
    angle: 22.5,
    range: [11.25, 33.75],
    element: 'Water',
    elementHi: 'जल तत्व',
    color: '#38BDF8',
    deity: 'Lord Dhanvantari & Apavatsa',
    deityHi: 'भगवान धन्वंतरि एवं आपवत्स',
    rulingPlanet: 'Jupiter (Guru)',
    rulingPlanetHi: 'बृहस्पति ग्रह',
    significance: 'Health, vitality, immunity, healing, medicine effectiveness and overall physical wellness.',
    significanceHi: 'उत्तम स्वास्थ्य, रोग प्रतिरोधक क्षमता, औषधियों का प्रभाव व निरोगी काया।',
    favorableFor: ['Medicine Cabinet', 'Healing & Recovery Room', 'Pooja Accessories', 'Water Filter / Urn'],
    favorableForHi: ['दवाइयों की पेटी', 'आरोग्य व विश्राम कक्ष', 'पूजा सामग्री', 'पीने का पानी'],
    unfavorableFor: ['Toilet', 'Dustbin', 'Kitchen stove', 'Dark gloomy storage'],
    unfavorableForHi: ['शौचालय', 'कूड़ेदान', 'रसोई का चूल्हा', 'अंधेरा गोदाम'],
    idealItems: ['Picture of Lord Dhanvantari', 'First Aid box', 'Holy Tulsi plant'],
    remedies: 'Keep daily medicines here with a picture of Lord Dhanvantari to accelerate healing and boost family vitality.',
    remediesHi: 'यहां दवाइयों का डिब्बा रखें व धन्वंतरि देव का चित्र लगाएं। इससे स्वास्थ्य में शीघ्र सुधार होता है।'
  },
  {
    id: 'NE',
    name: 'North-East (Ishanya)',
    nameHi: 'ईशान कोण (देव स्थान)',
    angle: 45,
    range: [33.75, 56.25],
    element: 'Water',
    elementHi: 'जल / आकाश तत्व',
    color: '#0EA5E9',
    deity: 'Lord Shiva & Ishana Deva',
    deityHi: 'भगवान शिव एवं ईशान देव',
    rulingPlanet: 'Jupiter & Ketu (Guru & Ketu)',
    rulingPlanetHi: 'बृहस्पति एवं केतु',
    significance: 'Supreme spirituality, enlightenment, wisdom, clarity of mind, meditation and divine grace.',
    significanceHi: 'परम आध्यात्मिक ऊर्जा, ज्ञान, मानसिक स्पष्टता, ध्यान, पूजा और ईश्वरीय कृपा।',
    favorableFor: ['Pooja Room / Mandir', 'Meditation / Yoga Area', 'Underground Water Tank', 'Open Terrace / Lawn'],
    favorableForHi: ['पूजा घर / मंदिर', 'ध्यान व योग स्थल', 'भूमिगत जल कुंड / बोरवेल', 'खुला प्रांगण'],
    unfavorableFor: ['Toilet (Major Vastu Dosh)', 'Kitchen', 'Septic Tank', 'Heavy staircase', 'Shoe rack'],
    unfavorableForHi: ['शौचालय (महा वास्तु दोष)', 'रसोई', 'सेप्टिक टैंक', 'भारी सीढ़ी', 'जूते-चप्पल'],
    idealItems: ['Marble Temple', 'Silver Om / Swastik', 'Gangajal urn', 'Copper water pot with Tulsi'],
    remedies: 'Strictly keep this zone lightweight, clean and fragrant. Place a brass or copper Vastu Dosh Nivaran Pyramid if a toilet exists nearby.',
    remediesHi: 'इस स्थान को सदैव हल्का, स्वच्छ व सुवासित रखें। गंगाजल का कलश व पीतल का वास्तु पिरामिड स्थापित करें।'
  },
  {
    id: 'ENE',
    name: 'East-North-East',
    nameHi: 'पूर्व-उत्तर-पूर्व (प्रसन्नता व मनोरंजन)',
    angle: 67.5,
    range: [56.25, 78.75],
    element: 'Air',
    elementHi: 'वायु / काष्ठ तत्व',
    color: '#22C55E',
    deity: 'Parjanya & Jayanta',
    deityHi: 'पर्जन्य एवं जयंत',
    rulingPlanet: 'Sun (Surya)',
    rulingPlanetHi: 'सूर्य देव',
    significance: 'Joy, recreation, cheerfulness, fun, rejuvenation and inner delight.',
    significanceHi: 'आनंद, मनोरंजन, स्फूर्ति, नवजीवन एवं पारिवारिक उल्लास।',
    favorableFor: ['Family Lounge', 'Music / Entertainment Room', 'Indoor Garden', 'Children Play area'],
    favorableForHi: ['पारिवारिक बैठक', 'संगीत / मनोरंजन कक्ष', 'लघु वाटिका', 'बच्चों के खेलकूद का स्थान'],
    unfavorableFor: ['Toilet', 'Heavy clutter', 'Dark red or black walls'],
    unfavorableForHi: ['शौचालय', 'कबाड़ व गंदगी', 'गहरा लाल/काला रंग'],
    idealItems: ['Green lush plant', 'Musical instruments', 'Family happy photos'],
    remedies: 'Keep indoor flowering plants or vibrant family portraits to infuse living spaces with laughter and vitality.',
    remediesHi: 'हरे-भरे पौधे व परिवार के प्रसन्नता भरे चित्र लगाएं। इससे घर में सदैव उमंग बनी रहती है।'
  },
  {
    id: 'E',
    name: 'East (Purva)',
    nameHi: 'पूर्व (इंद्र व सूर्य स्थान)',
    angle: 90,
    range: [78.75, 101.25],
    element: 'Air',
    elementHi: 'वायु तत्व',
    color: '#16A34A',
    deity: 'Lord Indra & Surya Deva',
    deityHi: 'भगवान इंद्र एवं सूर्य देव',
    rulingPlanet: 'Sun (Surya)',
    rulingPlanetHi: 'सूर्य ग्रह',
    significance: 'Social connections, government favor, prestige, leadership, public fame and networking.',
    significanceHi: 'सामाजिक प्रतिष्ठा, सरकारी लाभ, मान-सम्मान, नेतृत्व शक्ति व जनसंपर्क।',
    favorableFor: ['Main Entrance (E3, E4)', 'Living Hall', 'Study Room', 'Study Desk facing East', 'Balcony'],
    favorableForHi: ['मुख्य द्वार (E3, E4)', 'बैठक कक्ष', 'अध्ययन कक्ष', 'पूर्व मुखी स्टडी टेबल', 'बालकनी'],
    unfavorableFor: ['Toilet', 'Kitchen (causes social friction)', 'Closed opaque wall without windows'],
    unfavorableForHi: ['शौचालय', 'रसोई (सामाजिक विवाद)', 'बिना खिड़की की बंद भारी दीवार'],
    idealItems: ['Copper Sun (Surya Yantra)', 'Green plants', 'Large airy windows'],
    remedies: 'Hang a radiant Copper Sun on the East wall to amplify fame, authority, and public respect.',
    remediesHi: 'पूर्व दिशा की दीवार पर तांबे का सूर्य यंत्र लगाएं, जिससे मान-सम्मान व यश में अपार वृद्धि होती है।'
  },
  {
    id: 'ESE',
    name: 'East-South-East',
    nameHi: 'पूर्व-दक्षिण-पूर्व (मंथन व विश्लेषण)',
    angle: 112.5,
    range: [101.25, 123.75],
    element: 'Air',
    elementHi: 'वायु तत्व',
    color: '#84CC16',
    deity: 'Savita & Aruna',
    deityHi: 'सविता एवं अरुण देव',
    rulingPlanet: 'Rahu & Sun',
    rulingPlanetHi: 'राहु एवं सूर्य',
    significance: 'Thought processing, intellectual churning, analytical deliberation, and anxiety control.',
    significanceHi: 'गहन चिंतन, मंथन, विश्लेषणात्मक विचार व निर्णय शक्ति।',
    favorableFor: ['Analytical Workstation', 'Washing Machine', 'Mixer Grinder', 'Math / Research Study'],
    favorableForHi: ['अनुसंधान व विश्लेषण डेस्क', 'वाशिंग मशीन', 'मिक्सर ग्राइंडर', 'शोध कार्य'],
    unfavorableFor: ['Master Bedroom (causes severe overthinking)', 'Mandir / Pooja Room', 'Tijori'],
    unfavorableForHi: ['मुख्य शयनकक्ष (अत्यधिक चिंता व अनिद्रा)', 'पूजा घर', 'तिजोरी'],
    idealItems: ['Wooden churning pot model', 'Books on philosophy', 'Neutral cream tones'],
    remedies: 'Avoid sleeping here. Place a small wooden artifact or brass Kamdhenu Cow to turn overthinking into productive insights.',
    remediesHi: 'यहाँ सोने से बचें। चिंता निवारण हेतु कामधेनु गाय का पीतल का प्रतीक स्थापित करें।'
  },
  {
    id: 'SE',
    name: 'South-East (Agneya)',
    nameHi: 'आग्नेय कोण (अग्नि स्थान)',
    angle: 135,
    range: [123.75, 146.25],
    element: 'Fire',
    elementHi: 'अग्नि तत्व',
    color: '#EF4444',
    deity: 'Agni Deva (Fire Lord)',
    deityHi: 'अग्नि देव',
    rulingPlanet: 'Venus (Shukra)',
    rulingPlanetHi: 'शुक्र ग्रह',
    significance: 'Cash liquidity, passion, vitality, female health, culinary energy, and financial speed.',
    significanceHi: 'तरल धन, रसोई की अग्नि, उत्साह, स्त्रियों का स्वास्थ्य व वित्तीय सुरक्षा।',
    favorableFor: ['Kitchen (Primary / Best)', 'Electrical Inverter / Meter', 'Gas Burner', 'Boiler / Geyser'],
    favorableForHi: ['रसोई घर (सर्वोत्तम)', 'बिजली का मीटर / इनवर्टर', 'गैस स्टोव', 'गीजर / हीटर'],
    unfavorableFor: ['Underground Water Tank', 'Toilet (blocks cash flow)', 'Master Bed', 'Blue/Black colors'],
    unfavorableForHi: ['भूमिगत पानी की टंकी', 'शौचालय (धन हानि)', 'मास्टर बेडरूम', 'नीला/काला रंग'],
    idealItems: ['Gas stove facing East', 'Red bulb/candle', 'Venus Shukra Yantra'],
    remedies: 'Cook while facing East. Place a Red Jasper or lit red night bulb here to fire up consistent cash flow.',
    remediesHi: 'पूर्व दिशा की ओर मुख करके भोजन बनाएं। अग्नि तत्व को जागृत रखने हेतु एक मंद लाल बल्ब जलाएं।'
  },
  {
    id: 'SSE',
    name: 'South-South-East',
    nameHi: 'दक्षिण-दक्षिण-पूर्व (शक्ति व आत्मविश्वास)',
    angle: 157.5,
    range: [146.25, 168.75],
    element: 'Fire',
    elementHi: 'अग्नि तत्व',
    color: '#F97316',
    deity: 'Antariksha & Vitatha',
    deityHi: 'अंतरिक्ष एवं वितथ',
    rulingPlanet: 'Mars (Mangala)',
    rulingPlanetHi: 'मंगल ग्रह',
    significance: 'Physical stamina, zeal, warrior confidence, fearlessness, and muscular vigor.',
    significanceHi: 'शारीरिक बल, आत्मविश्वास, निर्भयता, पराक्रम व ऊर्जा।',
    favorableFor: ['Gym / Fitness Room', 'Security Guard Post', 'Executive Meeting Area', 'Kitchen Extension'],
    favorableForHi: ['व्यायामशाला / जिम', 'सुरक्षा गार्ड कक्ष', 'कार्यकारी बैठक', 'रसोई विस्तार'],
    unfavorableFor: ['Toilet (erodes confidence)', 'Underground water tank', 'Dark blue decor'],
    unfavorableForHi: ['शौचालय (आत्मविश्वास क्षीण होना)', 'पानी का गड्ढा', 'गहरा नीला रंग'],
    idealItems: ['Brass lion emblem', 'Dumbbells / Workout gear', 'Warm earthen hues'],
    remedies: 'Place a brass lion or red coral gemstone emblem to eliminate internal fear and awaken unshakable courage.',
    remediesHi: 'पीतल का सिंह प्रतीक अथवा तांबे का पिरामिड रखें। इससे भय दूर होकर आत्मविश्वास बढ़ता है।'
  },
  {
    id: 'S',
    name: 'South (Dakshin)',
    nameHi: 'दक्षिण (यम व मंगल स्थान)',
    angle: 180,
    range: [168.75, 191.25],
    element: 'Fire / Earth',
    elementHi: 'अग्नि व पृथ्वी तत्व',
    color: '#DC2626',
    deity: 'Lord Yama & Mars',
    deityHi: 'भगवान यमराज एवं मंगल देव',
    rulingPlanet: 'Mars (Mangala)',
    rulingPlanetHi: 'मंगल ग्रह',
    significance: 'Rest, deep sleep, brand recognition, public fame, authority, and relaxation.',
    significanceHi: 'विश्राम, प्रगाढ़ निद्रा, नाम व यश, मान-प्रतिष्ठा एवं शांति।',
    favorableFor: ['Bedroom', 'Heavy furniture', 'Media / PR room', 'Overhead water tank (partially)'],
    favorableForHi: ['शयनकक्ष', 'भारी अलमारी व फर्नीचर', 'प्रचार कक्ष', 'ऊपरी पानी की टंकी'],
    unfavorableFor: ['Main Entrance (unless S3/S4)', 'Underground water well', 'Toilet'],
    unfavorableForHi: ['अशुभ मुख्य द्वार', 'भूमिगत जल स्रोत', 'शौचालय'],
    idealItems: ['Certificates / Awards frame', 'Warm lighting', 'Solid wood bed'],
    remedies: 'Keep head towards South while sleeping for deepest physiological relaxation and balanced blood pressure.',
    remediesHi: 'सोते समय सिर दक्षिण दिशा की ओर रखें। इससे उत्तम नींद व दीर्घायु की प्राप्ति होती है।'
  },
  {
    id: 'SSW',
    name: 'South-South-West',
    nameHi: 'दक्षिण-दक्षिण-पश्चिम (विसर्जन व व्यय)',
    angle: 202.5,
    range: [191.25, 213.75],
    element: 'Earth',
    elementHi: 'पृथ्वी तत्व',
    color: '#A16207',
    deity: 'Gandharva & Mriga',
    deityHi: 'गंधर्व एवं मृग',
    rulingPlanet: 'Rahu (North Node)',
    rulingPlanetHi: 'राहु ग्रह',
    significance: 'Disposal of waste, shedding toxins, letting go of useless ties, and natural elimination.',
    significanceHi: 'व्यर्थ तत्वों का विसर्जन, विषाक्तता से मुक्ति, अपव्यय रोकना व निष्कासन।',
    favorableFor: ['Toilet / Commode (Best Zone in Vastu)', 'Dustbin', 'Drainage / Sewage Outflow'],
    favorableForHi: ['शौचालय (वास्तु में सर्वोत्तम स्थान)', 'कूड़ेदान', 'जल निकासी / सीवर पाइप'],
    unfavorableFor: ['Pooja Room (Critical Dosh)', 'Tijori (Destroys wealth)', 'Master Bedroom', 'Study Desk'],
    unfavorableForHi: ['पूजा घर (गंभीर दोष)', 'तिजोरी (धन की बर्बादी)', 'मास्टर बेडरूम', 'स्टडी टेबल'],
    idealItems: ['Toilet commode on yellow tape', 'Exhaust fan', 'Cleaning supplies'],
    remedies: 'If bedroom or study is stuck here, apply a 3-inch brass or yellow metal strip around the zone to block draining energy.',
    remediesHi: 'यदि यहाँ बेडरूम या तिजोरी हो तो 3-इंच चौड़ी पीली टेप अथवा पीतल की पट्टी से ऊर्जा को ब्लॉक करें।'
  },
  {
    id: 'SW',
    name: 'South-West (Nairutya)',
    nameHi: 'नैऋत्य कोण (स्थिरता व पितृ स्थान)',
    angle: 225,
    range: [213.75, 236.25],
    element: 'Earth',
    elementHi: 'पृथ्वी तत्व (स्थिरता)',
    color: '#CA8A04',
    deity: 'Nirriti & Pitru Devas',
    deityHi: 'निर्रिति देव एवं पितृगण',
    rulingPlanet: 'Rahu & Saturn (Rahu/Shani)',
    rulingPlanetHi: 'राहु एवं शनि',
    significance: 'Family headship, stability, relationship bonding, mastery of skills, and ancestral blessings.',
    significanceHi: 'गृहस्वामी का आधिपत्य, पारिवारिक स्थिरता, कौशल में निपुणता व पितरों का आशीर्वाद।',
    favorableFor: ['Master Bedroom (Best)', 'Tijori / Heavy Iron Safe (Opening North)', 'Heavy Storage', 'Family Photo Wall'],
    favorableForHi: ['मास्टर बेडरूम (सर्वोत्तम)', 'भारी तिजोरी (उत्तर की ओर खुलती हुई)', 'भारी सामान', 'पितरों/परिवार के चित्र'],
    unfavorableFor: ['Toilet (Destroys family peace)', 'Kitchen (Burns relationships)', 'Underground Water Tank', 'Balcony cut'],
    unfavorableForHi: ['शौचालय (पारिवारिक कलह)', 'रसोई (संबंधों में कटुता)', 'भूमिगत पानी का गड्ढा', 'कटा हुआ कोना'],
    idealItems: ['Golden / Yellow stone accents', 'Heavy wooden wardrobe', 'Golden family photo frame', 'Lead helix/pyramid'],
    remedies: 'Keep this zone highest and heaviest in the entire house. Hang a golden framed couple portrait to seal enduring marital bliss.',
    remediesHi: 'इस स्थान को घर में सबसे भारी व ऊंचा रखें। दक्षिण या पश्चिम दीवार से सटाकर भारी तिजोरी रखें जो उत्तर की ओर खुले।'
  },
  {
    id: 'WSW',
    name: 'West-South-West',
    nameHi: 'पश्चिम-दक्षिण-पश्चिम (विद्या व बचत)',
    angle: 247.5,
    range: [236.25, 258.75],
    element: 'Space',
    elementHi: 'आकाश / धातु तत्व',
    color: '#64748B',
    deity: 'Sugriva & Dauvarika',
    deityHi: 'सुग्रीव एवं दौवारिक',
    rulingPlanet: 'Mercury & Rahu',
    rulingPlanetHi: 'बुध एवं राहु',
    significance: 'Education, retention of knowledge, accumulation of fixed savings, and investment compounding.',
    significanceHi: 'विद्या अर्जन, स्मरण शक्ति, धन की बचत व पूंजी संचय।',
    favorableFor: ['Study Room & Desk', 'Bookshelf / Library', 'Savings Passbooks / Shares Vault', 'Children Study Bed'],
    favorableForHi: ['अध्ययन कक्ष व स्टडी टेबल', 'पुस्तकालय / बुकशेल्फ़', 'बचत डायरी / एफडी कागजात', 'विद्यार्थी कक्ष'],
    unfavorableFor: ['Toilet', 'Dustbin', 'Kitchen', 'Green plants (clashes with space element)'],
    unfavorableForHi: ['शौचालय', 'कूड़ेदान', 'रसोई', 'हरे-भरे पौधे (आकाश तत्व से विरोध)'],
    idealItems: ['Saraswati Yantra', 'Study table facing East/North', 'Pen stand with yellow pens'],
    remedies: 'Place textbooks and financial investment documents here. Install a brass pen stand or Saraswati Yantra for academic excellence.',
    remediesHi: 'बच्चों की पढ़ाई की मेज यहाँ रखें व सरस्वती यंत्र लगाएं। इससे स्मरण शक्ति और परीक्षा में सफलता बढ़ती है।'
  },
  {
    id: 'W',
    name: 'West (Paschim)',
    nameHi: 'पश्चिम (वरुण व शनि स्थान)',
    angle: 270,
    range: [258.75, 281.25],
    element: 'Space',
    elementHi: 'आकाश / धातु तत्व',
    color: '#475569',
    deity: 'Lord Varuna & Pushpadanta',
    deityHi: 'भगवान वरुण एवं पुष्पदंत',
    rulingPlanet: 'Saturn (Shani)',
    rulingPlanetHi: 'शनि ग्रह',
    significance: 'Material gains, profit margins, fulfillment of desires, business contracts, and closure.',
    significanceHi: 'व्यापारिक लाभ, इच्छा पूर्ति, प्राप्तियां, कार्य सिद्धि व संपत्तियों का विस्तार।',
    favorableFor: ['Dining Room', 'Accountant / Sales Desk', 'Overhead Water Tank', 'Master Bed (Alternative)', 'Locker (Opening East)'],
    favorableForHi: ['भोजन कक्ष (डाइनिंग)', 'सेल्स / अकाउंट्स डेस्क', 'छत पर पानी की टंकी', 'शयनकक्ष', 'तिजोरी (पूर्व मुखी)'],
    unfavorableFor: ['Underground Tank', 'Basement', 'Green or Red shades'],
    unfavorableForHi: ['भूमिगत जलकुंड', 'तहखाना', 'हरा या लाल रंग'],
    idealItems: ['Round White Dining Table', 'Metal art', 'Shani / Varuna Yantra'],
    remedies: 'Place a round metallic plate or dining table here to multiply profitability and manifest personal wishes effortlessly.',
    remediesHi: 'सफेद या ग्रे रंग के धातु के पात्र रखें। पश्चिम दिशा में बैठकर भोजन करने से स्वास्थ्य व लाभ में वृद्धि होती है।'
  },
  {
    id: 'WNW',
    name: 'West-North-West',
    nameHi: 'पश्चिम-उत्तर-पश्चिम (अवसाद मुक्ति व रोदन)',
    angle: 292.5,
    range: [281.25, 303.75],
    element: 'Air / Space',
    elementHi: 'आकाश व वायु तत्व',
    color: '#94A3B8',
    deity: 'Asura & Shosha',
    deityHi: 'असुर एवं शोष',
    rulingPlanet: 'Moon & Saturn',
    rulingPlanetHi: 'चंद्र एवं शनि',
    significance: 'Emotional detoxification, weeping away sadness, catharsis, and mental decluttering.',
    significanceHi: 'मानसिक तनाव मुक्ति, भावनाओं का रेचन, दुख निवारण व मन की शांति।',
    favorableFor: ['Toilet (Good)', 'Washing Machine', 'Detox / Spa Area', 'Dustbin'],
    favorableForHi: ['शौचालय (शुभ)', 'वाशिंग मशीन', 'विश्राम व तनाव मुक्ति स्थल', 'कूड़ेदान'],
    unfavorableFor: ['Master Bedroom (leads to clinical depression)', 'Mandir / Temple', 'Tijori', 'Living Room'],
    unfavorableForHi: ['मास्टर बेडरूम (अवसाद व उदासी)', 'मंदिर / पूजा घर', 'तिजोरी', 'मुख्य बैठक'],
    idealItems: ['White windchime', 'Sea salt pot', 'Round mirror'],
    remedies: 'Never sleep in WNW. If a bedroom exists here, place a brass deer figurine or an open bowl of Himalayan sea salt to purge negativity.',
    remediesHi: 'यहाँ सोने से बचें। नकारात्मक ऊर्जा के अवशोषण हेतु एक कटोरी में समुद्री नमक रखें।'
  },
  {
    id: 'NW',
    name: 'North-West (Vayavya)',
    nameHi: 'वायव्य कोण (वायु व चंद्र स्थान)',
    angle: 315,
    range: [303.75, 326.25],
    element: 'Air',
    elementHi: 'वायु तत्व (गतिशीलता)',
    color: '#CBD5E1',
    deity: 'Lord Vayu (Wind God)',
    deityHi: 'पवन देव (वायु देव)',
    rulingPlanet: 'Moon (Chandra)',
    rulingPlanetHi: 'चंद्र ग्रह',
    significance: 'Banking support, helpful friends, government subsidies, speed of finished goods, and movement.',
    significanceHi: 'बैंकिंग सहयोग, मित्रों व संबंधियों से सहायता, तैयार माल की बिक्री व गतिशीलता।',
    favorableFor: ['Guest Bedroom', 'Finished Goods Store', 'Daughter Bedroom', 'Garage / Vehicle Parking', 'Toilet (Acceptable)'],
    favorableForHi: ['अतिथि कक्ष', 'तैयार उत्पाद / स्टॉक', 'कन्या का शयनकक्ष', 'वाहन पार्किंग', 'शौचालय'],
    unfavorableFor: ['Tijori / Long-term cash (causes cash to fly away)', 'Master Bedroom for family head', 'Heavy underground tank'],
    unfavorableForHi: ['स्थिर तिजोरी (धन तुरंत खर्च होना)', 'गृहस्वामी का बेडरूम', 'गहरा कुआं'],
    idealItems: ['Pair of white flying horses', 'Round metallic clock', 'Silver bell'],
    remedies: 'Place a pair of white running horses or a silver moon yantra to unlock instant bank loan approvals and international support.',
    remediesHi: 'सफेद दौड़ते हुए घोड़ों का चित्र लगाएं। इससे बैंक लोन और व्यापारिक सहयोग बिना रुकावट प्राप्त होता है।'
  },
  {
    id: 'NNW',
    name: 'North-North-West',
    nameHi: 'उत्तर-उत्तर-पश्चिम (आकर्षण व माधुर्य)',
    angle: 337.5,
    range: [326.25, 348.75],
    element: 'Water / Air',
    elementHi: 'जल व वायु तत्व',
    color: '#7DD3FC',
    deity: 'Roga & Naga',
    deityHi: 'रोग एवं नाग देव',
    rulingPlanet: 'Moon & Venus',
    rulingPlanetHi: 'चंद्र एवं शुक्र',
    significance: 'Romantic attraction, sensual vitality, interpersonal charm, and physical intimacy.',
    significanceHi: 'वैवाहिक आकर्षण, सौंदर्य, प्रेम माधुर्य व आपसी अनुराग।',
    favorableFor: ['Newlywed Couple Bedroom', 'Dressing Room / Vanity', 'Wardrobe with perfumes', 'Living room corner'],
    favorableForHi: ['नवविवाहित युगल कक्ष', 'श्रृंगार कक्ष / ड्रेसिंग टेबल', 'इत्र व सौंदर्य प्रसाधन', 'बैठक'],
    unfavorableFor: ['Toilet (causes intimacy disinterest)', 'Pooja room', 'Children study room'],
    unfavorableForHi: ['शौचालय', 'पूजा घर', 'बच्चों का अध्ययन कक्ष'],
    idealItems: ['Aroma diffuser with rose oil', 'Dressing mirror', 'Pastel blue/cream bedsheets'],
    remedies: 'Keep fresh jasmine or rose essential oils in this corner to rekindle passion and affection between life partners.',
    remediesHi: 'गुलाब या चमेली का सुगंधित डिफ्यूज़र रखें। इससे दांपत्य जीवन में अपार मिठास और आकर्षण बना रहता है।'
  }
];

// Brahmasthan Definition (Center of the House)
export const BRAHMASTHAN_DATA = {
  name: 'Brahmasthan (Cosmic Center)',
  nameHi: 'ब्रह्मस्थान (घर का नाभिकीय केंद्र)',
  element: 'Ether / Akash (आकाश तत्व)',
  rulingDeity: 'Lord Brahma (सृष्टिकर्ता ब्रह्मा)',
  significance: 'The epicentre of prana (vital life force) that nourishes all 16 directions and household inhabitants.',
  significanceHi: 'प्राण ऊर्जा का मूल स्रोत जो घर की सभी 16 दिशाओं और सदस्यों को पोषण प्रदान करता है।',
  rules: [
    'Must be kept 100% open, luminous, level, and clean.',
    'Strictly NO heavy pillars, columns, structural load-bearing beams or staircases.',
    'Strictly NO toilets, septic tanks, kitchens, or underground borewells.',
    'Ideal for open courtyard (Aangan), crystal hanging chandelier, or subtle floral Rangoli.'
  ],
  rulesHi: [
    'इसे सदैव खुला, हवादार, समतल और अत्यंत स्वच्छ रखना चाहिए।',
    'यहाँ कोई भारी खंभा, बीम, दीवार या सीढ़ियाँ नहीं होनी चाहिए।',
    'यहाँ शौचालय, सेप्टिक टैंक, रसोई या बोरवेल होना महा-दोष माना जाता है।',
    'खुला आँगन, क्रिस्टल झूमर या सुंदर रंगोली के लिए सर्वोत्तम।'
  ],
  remedies: 'If heavy load or toilet exists in Brahmasthan, bury 4 copper pyramid plates on four boundaries and hang a 30mm faceted optical crystal in the ceiling center.',
  remediesHi: 'यदि ब्रह्मस्थान में बीम या दोष हो तो छत के केंद्र में स्फटिक क्रिस्टल बॉल लटकाएं और पीतल के पिरामिड स्थापित करें।'
};

// SIGNIFICANT PLACEMENTS (Deep-dive rules for Pooja Room, Tijori, Kitchen, etc.)
export const SIGNIFICANT_PLACEMENTS: SignificantPlacement[] = [
  {
    id: 'pooja_room',
    title: 'Pooja Room & Home Mandir',
    titleHi: 'पूजा घर एवं गृह मंदिर',
    category: 'Spiritual',
    icon: 'Sparkles',
    bestZones: ['NE (Ishanya - Divine Zone)', 'NNE (Health & Purity)', 'East (Purva - Surya Zone)', 'North (Kubera Zone)'],
    secondaryZones: ['West (Paschim - Devotee facing East)'],
    forbiddenZones: ['SSW (Disposal)', 'SW (Ancestors / Master Bed)', 'SE (Fire / Aggression)', 'WNW (Depression)', 'Under Staircase', 'Attached to Toilet'],
    deityOrLord: 'Lord Shiva, Jupiter (Guru) & Ishana Deva',
    orientationGuidelines: 'Idols should ideally face East or West. The devotee praying should face East (towards rising Sun) or North (towards Kubera).',
    orientationGuidelinesHi: 'मूर्तियों का मुख पश्चिम या पूर्व की ओर हो ताकि पूजा करते समय साधक का मुख पूर्व (सूर्य) अथवा उत्तर (कुबेर) दिशा की ओर रहे।',
    vastuRules: [
      'Pooja room must be lightweight, elevated on marble or solid teak wood with a raised plinth (4-6 inches).',
      'The Akhand Jyot / Diya must always be placed in the South-East (Agneya) corner inside the temple.',
      'Water vessel (Gangajal / Achamani) should be placed in the North-East corner of the mandir altar.',
      'Never build a temple under a staircase, inside a bedroom, or directly sharing a common wall with a bathroom/toilet.',
      'Keep temple walls in sacred soothing tones: off-white, light yellow, cream, or light sky blue.'
    ],
    vastuRulesHi: [
      'मंदिर हल्का, संगमरमर या सागौन की लकड़ी का बना हो और फर्श से 4-6 इंच ऊंचा हो।',
      'दीपक या अखंड ज्योति सदैव मंदिर के आग्नेय कोण (दक्षिण-पूर्व) में प्रज्वलित करें।',
      'गंगाजल व पूजा का जल पात्र मंदिर के ईशान कोण (उत्तर-पूर्व) में रखें।',
      'सीढ़ी के नीचे, शयनकक्ष में या शौचालय की दीवार से सटाकर मंदिर कभी न बनाएं।',
      'मंदिर की दीवारों पर हल्का पीला, सफेद या क्रीम रंग सर्वश्रेष्ठ होता है।'
    ],
    doshaEffects: 'Mandir in South-West causes severe family disputes; in South-East triggers frequent health burnouts and anger; in SSW drains spiritual energy.',
    doshaEffectsHi: 'नैऋत्य में मंदिर होने से पारिवारिक अशांति; आग्नेय में होने से क्रोध व स्वास्थ्य हानि; तथा SSW में होने से भक्ति का फल नष्ट होता है।',
    remediesWithoutDemolition: [
      'Place a solid brass Vastu Pyramid or Silver Swastik on the temple door entrance.',
      'Keep a copper vessel with fresh water and Holy Basil (Tulsi) leaves replenished daily in NE.',
      'Hang a small brass bell (Ghanti) on the right side and ring morning and evening to disperse stagnant energies.',
      'Apply white marble base under the idols if the temple cannot be shifted immediately.'
    ],
    remediesWithoutDemolitionHi: [
      'मंदिर के मुख्य द्वार पर चांदी का स्वस्तिक अथवा पीतल का वास्तु पिरामिड लगाएं।',
      'ईशान कोण में तांबे के लोटे में ताजा जल व तुलसी दल भरकर प्रतिदिन बदलें।',
      'सुबह-शाम पीतल की घंटी बजाएं जिससे नकारात्मक ऊर्जा का नाश हो।',
      'यदि मंदिर को तुरंत हटाना संभव न हो तो मूर्तियों के नीचे सफेद संगमरमर की चौकी रखें।'
    ]
  },
  {
    id: 'tijori_safe',
    title: 'Tijori, Cash Locker & Wealth Vault',
    titleHi: 'तिजोरी, धन लॉकर एवं कुबेर भंडार',
    category: 'Wealth',
    icon: 'Shield',
    bestZones: ['North (Kubera - Wealth Inflow)', 'South-West (Nairutya - Heavy Vault opening North)', 'West (Gains & Fulfillment opening East)'],
    secondaryZones: ['NNW (Liquid Cash for business)', 'WSW (Savings & Fixed Deposits)'],
    forbiddenZones: ['SE (Fire burns wealth / unexpected expenses)', 'SSW (Disposal drains savings)', 'NW (Fluctuating money)', 'NE (Heavy load crushes mental peace)'],
    deityOrLord: 'Goddess Mahalakshmi, Lord Kubera & Mercury',
    orientationGuidelines: 'Place the safe against the South or West wall so that when opened, the door opens smoothly towards the NORTH (Kubera) or EAST (Indra/Surya).',
    orientationGuidelinesHi: 'तिजोरी को दक्षिण अथवा पश्चिम की दीवार से सटाकर रखें ताकि उसका द्वार खोलते समय उत्तर (कुबेर) अथवा पूर्व (इंद्र) दिशा की ओर खुले।',
    vastuRules: [
      'The Tijori should be placed on a raised wooden plank or solid platform, never directly touching the bare floor.',
      'The locker should not wobble or squeak upon opening; sound of opening should be smooth and silent.',
      'Stick a small clean mirror on the inner side of the locker door facing the cash, creating a divine double reflection of wealth.',
      'Keep a clean red or bright yellow velvet cloth under the cash and gold jewellery.',
      'Keep a silver coin of Goddess Lakshmi & Lord Ganesha or an energized Kubera Yantra inside.'
    ],
    vastuRulesHi: [
      'तिजोरी को सीधे फर्श पर न रखकर लकड़ी के पटरे या आसन पर स्थापित करें।',
      'तिजोरी का दरवाजा खुलते समय कर्कश आवाज नहीं होनी चाहिए, वह समतल रहे।',
      'तिजोरी के अंदर की दीवार पर एक छोटा सा शीशा लगाएं जो नकदी को प्रतिबिंबित (Double) करे।',
      'धन व आभूषणों के नीचे लाल अथवा पीले रंग का रेशमी कपड़ा बिछाएं।',
      'अंदर चांदी का लक्ष्मी-गणेश सिक्का अथवा सिद्ध कुबेर यंत्र रखें।'
    ],
    doshaEffects: 'Tijori in South-East causes sudden financial losses or medical bills; in SSW results in zero savings; in NE creates severe mental anxiety and head burdens.',
    doshaEffectsHi: 'आग्नेय में तिजोरी रखने से अप्रत्याशित खर्चे व चिकित्सा बिल बढ़ते हैं; SSW में रखने से बचत शून्य हो जाती है; ईशान में भारी तिजोरी से मानसिक तनाव होता है।',
    remediesWithoutDemolition: [
      'If Tijori opens towards South, place an energized Kubera Yantra facing North inside.',
      'Keep 12 green cardamom pods (Elaichi) and a Supari wrapped in yellow cloth inside the cash box.',
      'Spray Gangajal with turmeric water on the outer body on Friday mornings.',
      'Never keep unpaid bills, loan notices, or discarded papers inside the holy cash locker.'
    ],
    remediesWithoutDemolitionHi: [
      'यदि तिजोरी का मुख दक्षिण की ओर खुलता हो, तो अंदर उत्तर मुखी सिद्ध कुबेर यंत्र रखें।',
      'पीले कपड़े में 12 हरी इलायची और 1 सुपारी लपेटकर तिजोरी में रखें।',
      'शुक्रवार के दिन तिजोरी पर हल्दी मिले गंगाजल का छिड़काव करें।',
      'तिजोरी के अंदर कभी भी बिजली के बिल, ऋण के कागजात या फटे पुराने पर्चे न रखें।'
    ]
  },
  {
    id: 'kitchen',
    title: 'Kitchen & Cooking Hearth',
    titleHi: 'रसोई घर एवं पाकशाला',
    category: 'Living',
    icon: 'Zap',
    bestZones: ['South-East (Agneya - Primary Fire Zone)', 'North-West (Vayavya - Air/Movement Secondary)'],
    secondaryZones: ['East (Purva - with cook facing East)'],
    forbiddenZones: ['North-East (Water + Fire collision - Fatal Vastu Dosh)', 'South-West (Burns Family Relationships)', 'North (Burns Career Opportunities)'],
    deityOrLord: 'Lord Agni (Fire God) & Goddess Annapurna',
    orientationGuidelines: 'The cook must stand facing EAST while preparing meals. Sink (water) must be in NE of kitchen; Gas stove (fire) in SE.',
    orientationGuidelinesHi: 'भोजन बनाते समय रसोइए का मुख पूर्व दिशा की ओर होना चाहिए। पानी का सिंक ईशान कोण में तथा गैस चूल्हा आग्नेय कोण में हो।',
    vastuRules: [
      'Maintain at least 3 to 4 feet distance between the Gas Stove (Fire) and Water Sink (Water element) to avoid elemental conflict.',
      'Never place the cooking stove directly under a structural ceiling beam or adjacent to a toilet wall.',
      'Refrigerator should be stationed in South, South-East, or North-West of the kitchen.',
      'Storage of heavy grains and spices should be in the South or West cabinets of the kitchen.',
      'Use natural soothing colors: pastel green, warm cream, light peach, or soft terracotta.'
    ],
    vastuRulesHi: [
      'गैस चूल्हे (अग्नि) और सिंक (जल) के बीच कम से कम 3-4 फीट की दूरी रखें ताकि तत्व-टकराव न हो।',
      'चूल्हा कभी भी छत के बीम के नीचे या शौचालय की दीवार से सटाकर न रखें।',
      'फ्रिज को रसोई के दक्षिण, आग्नेय अथवा वायव्य कोण में रखें।',
      'अनाज व भारी दालों का भंडारण दक्षिण या पश्चिम दिशा की अलमारियों में करें।',
      'रसोई की दीवारों पर हल्का हरा, क्रीम या हल्का नारंगी रंग उपयुक्त होता है।'
    ],
    doshaEffects: 'Kitchen in North-East leads to severe chronic digestive illnesses, neurological tensions, and family disputes.',
    doshaEffectsHi: 'ईशान कोण में रसोई होने से गंभीर उदर रोग, मानसिक तनाव और आर्थिक संकट उत्पन्न होता है।',
    remediesWithoutDemolition: [
      'If stove is in North-East, place a green marble slab (Baroda Green) under the gas stove.',
      'If stove and sink are touching, place a wooden cutting board or copper strip between them.',
      'Hang a small brass Sun emblem or lit red bulb in the SE corner to balance fire vitality.',
      'Place a picture of Goddess Annapurna on the East wall of the kitchen.'
    ],
    remediesWithoutDemolitionHi: [
      'यदि चूल्हा ईशान कोण में हो तो उसके नीचे हरे रंग का संगमरमर (Green Marble) स्लैब रखें।',
      'यदि चूल्हा व सिंक पास-पास हों तो बीच में लकड़ी का बोर्ड अथवा तांबे की पट्टी लगाएं।',
      'आग्नेय कोण में एक छोटा लाल बल्ब या तांबे का सूर्य यंत्र स्थापित करें।',
      'रसोई की पूर्व दीवार पर मां अन्नपूर्णा का पावन चित्र लगाएं।'
    ]
  },
  {
    id: 'master_bedroom',
    title: 'Master Bedroom & Bed Headboard',
    titleHi: 'मुख्य शयनकक्ष एवं शयन दिशा',
    category: 'Living',
    icon: 'Award',
    bestZones: ['South-West (Nairutya - Supreme Stability & Mastery)', 'South (Deep Rest)', 'West (Gains & Fulfillment)'],
    secondaryZones: ['North-West (Good for young married couples)'],
    forbiddenZones: ['North-East (Causes over-intellectual insomnia & anxiety)', 'South-East (Aggression & marital friction)', 'East-South-East (Overthinking)'],
    deityOrLord: 'Nirriti Deva & Lord Yama (Planets: Rahu & Saturn)',
    orientationGuidelines: 'Headboard must strictly face SOUTH (Best for blood circulation & Earth magnetic alignment) or EAST (Memory & wisdom). Never sleep with head towards North.',
    orientationGuidelinesHi: 'सोते समय सिर सदैव दक्षिण (सर्वोत्तम) अथवा पूर्व दिशा में होना चाहिए। उत्तर दिशा में सिर करके कभी न सोएं।',
    vastuRules: [
      'The bed should be made of solid natural wood, free from creaking metallic joints or sharp angular storage.',
      'Never place a dressing mirror or TV screen directly reflecting the sleeping body on the bed.',
      'Do not sleep directly underneath an exposed overhead concrete beam.',
      'Keep master bedroom walls in warm grounding earth tones: almond, ivory, beige, or golden sand.'
    ],
    vastuRulesHi: [
      'पलंग ठोस लकड़ी का होना चाहिए और उसमें कोई नुकीला कोना नहीं होना चाहिए।',
      'बिस्तर के सामने कोई ऐसा दर्पण या टीवी न हो जिसमें सोते समय शरीर का प्रतिबिंब दिखाई दे।',
      'छत के भारी बीम के ठीक नीचे पलंग न लगाएं।',
      'कमरे की दीवारों पर बादामी, बेज, क्रीम या हल्का भूरा रंग सबसे उत्तम होता है।'
    ],
    doshaEffects: 'Master bedroom in South-East causes angry arguments and hypertension; in North-East impairs deep sleep and causes spiritual detachment from worldly duties.',
    doshaEffectsHi: 'आग्नेय में सोने से दांपत्य जीवन में कलह व रक्तचाप की समस्या; ईशान में सोने से गहरी नींद न आना व वैराग्य की भावना उत्पन्न होती है।',
    remediesWithoutDemolition: [
      'Cover all mirrors facing the bed at night with a soft curtain.',
      'If head is forced towards West, keep a South-aligned copper pyramid under the mattress.',
      'Place a pair of Rose Quartz lovebirds or a brass peacock on the bedside table for relationship harmony.'
    ],
    remediesWithoutDemolitionHi: [
      'बिस्तर के सामने वाले शीशे पर रात के समय पर्दा डाल दें।',
      'यदि सिर पश्चिम में रखना पड़े, तो गद्दे के नीचे तांबे का पिरामिड स्थापित करें।',
      'कमरे में रोज़ क्वार्ट्ज़ स्टोन अथवा पीतल का मोर युगल रखें जिससे दांपत्य संबंध मधुर रहें।'
    ]
  },
  {
    id: 'study_room',
    title: 'Study Room, Home Office & Workstation',
    titleHi: 'अध्ययन कक्ष, गृह कार्यालय एवं कार्यस्थल',
    category: 'Living',
    icon: 'BookOpen',
    bestZones: ['WSW (West-South-West - Knowledge & Retention)', 'North (Career & Wealth Inflow)', 'East (Intellect & Brilliance)', 'NE (Wisdom)'],
    secondaryZones: ['West (Paschim - Systematic Execution)'],
    forbiddenZones: ['SSW (Disposal of Memory)', 'ESE (Severe Overthinking)', 'SE (Restless Distractions)'],
    deityOrLord: 'Goddess Saraswati & Lord Ganesha (Planets: Mercury & Jupiter)',
    orientationGuidelines: 'While studying or working on laptop, always sit facing NORTH (towards Kubera/Mercury) or EAST (towards Sun).',
    orientationGuidelinesHi: 'पढ़ते या कंप्यूटर पर कार्य करते समय विद्यार्थी/कार्यकर्ता का मुख सदैव उत्तर अथवा पूर्व दिशा की ओर होना चाहिए।',
    vastuRules: [
      'The study desk should be rectangular or square (avoid circular, triangular, or L-shaped tables without grounding).',
      'Keep a solid wall behind your back while sitting, not an open door or empty void (ensures authoritative support).',
      'Keep the study desk clean and clutter-free; do not stack books in a messy chaotic pile.',
      'Place a natural green plant or crystal globe on the North-East corner of the study desk.'
    ],
    vastuRulesHi: [
      'अध्ययन की मेज चौकोर या आयताकार हो, गोल या कटे हुए आकार की न हो।',
      'बैठते समय पीठ के पीछे ठोस दीवार होनी चाहिए, कोई खुला दरवाजा या खिड़की नहीं।',
      'मेज पर अनावश्यक कबाड़ या किताबों का ढेर न लगाएं।',
      'स्टडी टेबल के उत्तर-पूर्व कोने पर स्फटिक ग्लोब अथवा हरा पौधा रखें।'
    ],
    doshaEffects: 'Studying in SSW causes students to forget whatever they study; in ESE creates anxiety and analysis paralysis before exams.',
    doshaEffectsHi: 'SSW में बैठकर पढ़ने से पढ़ा हुआ सब भूल जाता है; ESE में पढ़ने से परीक्षा के समय घबराहट होती है।',
    remediesWithoutDemolition: [
      'Install an energized Saraswati Yantra or Gayatri Mantra plaque on the wall in front of the desk.',
      'Keep a small brass pen stand holding 4 yellow or green pens in WSW/North.',
      'Place an educational pyramid on the desk corner to amplify concentration.'
    ],
    remediesWithoutDemolitionHi: [
      'सामने की दीवार पर सिद्ध सरस्वती यंत्र अथवा गायत्री मंत्र का फ्रेम लगाएं।',
      'मेज पर पीतल का पेन स्टैंड और 4 हरे/पीले पेन रखें।',
      'एकाग्रता बढ़ाने हेतु स्टडी टेबल पर स्फटिक अथवा तांबे का एजुकेशन पिरामिड रखें।'
    ]
  },
  {
    id: 'toilet_bath',
    title: 'Toilet, Bathroom & Septic Tank',
    titleHi: 'शौचालय, स्नानागार एवं सेप्टिक टैंक',
    category: 'Utilities',
    icon: 'Layers',
    bestZones: ['SSW (South-South-West - Zone of Disposal - Ideal)', 'WNW (Detoxification & Depression Release)', 'ESE (Churning & Elimination)'],
    secondaryZones: ['NW (Vayavya - with copper/brass threshold)'],
    forbiddenZones: ['North-East (Devastating Dosh - Cancer, Paralysis, Bankruptcy)', 'South-West (Destroys Relationships & Stability)', 'Brahmasthan (Cosmic Ruin)', 'North', 'East'],
    deityOrLord: 'Rahu & Saturn (Disposal of Impurities)',
    orientationGuidelines: 'Commode seat should be aligned such that the person sits facing NORTH or SOUTH. Never face East or West on commode.',
    orientationGuidelinesHi: 'शौचालय की सीट इस प्रकार हो कि बैठते समय व्यक्ति का मुख उत्तर या दक्षिण की ओर रहे।',
    vastuRules: [
      'Keep toilet door tightly closed at all times to prevent negative toxic sha-chi from spreading into the home.',
      'Septic tank must strictly be placed in SSW, WNW, or NW—never in NE, SW, SE, or Center.',
      'Bathroom geyser / water heater should be in South-East corner of the bathroom.',
      'Washbasin and mirror should be on North or East wall of bathroom.'
    ],
    vastuRulesHi: [
      'शौचालय का दरवाजा सदैव बंद रखें ताकि नकारात्मक ऊर्जा घर में न फैले।',
      'सेप्टिक टैंक सदैव SSW, WNW या वायव्य में ही बनाएं; कभी भी ईशान या नैऋत्य में न बनाएं।',
      'गीजर सदैव बाथरूम के आग्नेय कोण (दक्षिण-पूर्व) में लगाएं।',
      'वॉशबेसिन और शीशा बाथरूम की उत्तर या पूर्व दीवार पर लगाएं।'
    ],
    doshaEffects: 'Toilet in North-East drains family fortunes and causes severe chronic medical conditions; in South-West ruins marriage and causes heavy debts.',
    doshaEffectsHi: 'ईशान में शौचालय होने से असाध्य रोग व भारी धन हानि होती है; नैऋत्य में होने से गृहस्वामी का पतन और ऋण बढ़ता है।',
    remediesWithoutDemolition: [
      'Apply a 3-inch wide Brass strip (for SW/W), Copper strip (for SE/S), or Zinc/Aluminum strip (for NE) embedded around the commode base to magnetically sever the zone.',
      'Keep an open ceramic bowl filled with coarse Himalayan Pink Rock Salt in the toilet; replace salt weekly.',
      'Keep a live Snake Plant (Sansevieria) in a ceramic pot inside the bathroom to absorb toxic emanations.'
    ],
    remediesWithoutDemolitionHi: [
      'कमोड के चारों ओर 3-इंच चौड़ी धातु की पट्टी (पीतल/तांबा/एल्युमिनियम) या रंगीन टेप लगाकर ऊर्जा को ब्लॉक करें।',
      'बाथरूम में एक चीनी मिट्टी के कटोरे में खड़ा समुद्री नमक रखें और हर सप्ताह बदलें।',
      'विषाक्त ऊर्जा को सोखने के लिए बाथरूम में स्नेक प्लांट (Snake Plant) रखें।'
    ]
  },
  {
    id: 'main_entrance',
    title: 'Main Entrance & Doorway (Mahadwara)',
    titleHi: 'मुख्य द्वार एवं महाद्वार (32 पद विन्यास)',
    category: 'Structure',
    icon: 'Compass',
    bestZones: ['N3 (Mukhya) & N4 (Bhallat) in North - Immense Wealth', 'E3 (Jayanta) & E4 (Indra) in East - High Fame & Power', 'S3 (Grihakshata) & S4 (Vitatha) in South - Prosperity', 'W3 (Sugriva) & W4 (Pushpadanta) in West - Profits & Growth'],
    secondaryZones: ['E5 (Surya)', 'N5 (Soma)'],
    forbiddenZones: ['S1, S2, S5 (Fear & poverty)', 'SW (Nairutya - Heavy Losses)', 'SE (Fire accidents & legal disputes)', 'NW (Instability & debt)'],
    deityOrLord: 'Goddess Lakshmi, Lord Ganesha, Dwarapalakas',
    orientationGuidelines: 'Main door should be the largest door in the house, opening clockwise (Dakshinavarta) into the home with a clear welcoming threshold.',
    orientationGuidelinesHi: 'मुख्य द्वार घर का सबसे बड़ा दरवाजा होना चाहिए, जो अंदर की ओर दक्षिणावर्त (Clockwise) खुले और उसमें एक सुंदर देहरी (चौखट) हो।',
    vastuRules: [
      'Ensure the main doorway has a proper raised threshold (Dehleez) made of wood or marble to prevent wealth from slipping away.',
      'Never place a shoe rack, dustbin, or broken clutter directly in front of the main entrance.',
      'The door must not make any creaking or screeching sound when opening or closing.',
      'Adorn the main doorway with sacred symbols: Copper Swastik, Om, Trishul, and fresh Toran of Mango/Ashoka leaves.'
    ],
    vastuRulesHi: [
      'मुख्य द्वार पर सुंदर देहरी (चौखट) अवश्य हो, जिससे घर की लक्ष्मी बाहर न जाए।',
      'मुख्य द्वार के ठीक सामने जूते-चप्पल, कूड़ेदान या कबाड़ कभी न रखें।',
      'दरवाजा खोलते या बंद करते समय चरमराहट की आवाज नहीं होनी चाहिए।',
      'द्वार पर तांबे का स्वस्तिक, ॐ, शुभ-लाभ और आम/अशोक के पत्तों का तोरण लगाएं।'
    ],
    doshaEffects: 'Defective entrance in South-West brings chronic legal hassles and abrupt financial collapse; in South-East triggers frequent domestic friction.',
    doshaEffectsHi: 'नैऋत्य में मुख्य द्वार होने से मुकदमों का सामना व भारी नुकसान होता है; आग्नेय में होने से घर में कलह व आग का भय रहता है।',
    remediesWithoutDemolition: [
      'Hang a Brass / Copper Vastu Trishul-Om-Swastik emblem on both sides of the main frame.',
      'Place a pair of brass elephants with trunks raised upwards inside the entrance facing the door.',
      'Place yellow/red tape at the threshold to neutralize inauspicious entry energy.'
    ],
    remediesWithoutDemolitionHi: [
      'चौखट के दोनों ओर तांबे अथवा पीतल का त्रिशक्ति (त्रिशूल-ॐ-स्वस्तिक) यंत्र लगाएं।',
      'द्वार के अंदर की ओर ऊपर सूंड किए हुए पीतल के हाथी का जोड़ा रखें।',
      'अशुभ पद में द्वार होने पर देहरी पर उपयुक्त धातु की पट्टी या रंगीन टेप लगाएं।'
    ]
  },
  {
    id: 'water_tanks',
    title: 'Water Tanks (Underground & Overhead)',
    titleHi: 'जल संचय (भूमिगत एवं छत की टंकी)',
    category: 'Utilities',
    icon: 'Activity',
    bestZones: ['Underground Tank / Borewell: North-East (Ishanya) & North', 'Overhead Tank (Heavy): South-West (Nairutya), South, or West'],
    secondaryZones: ['Underground in East', 'Overhead in West-South-West'],
    forbiddenZones: ['Underground in SW (Catastrophic Dosh - Death/Loss of householder)', 'Overhead in NE (Crushes wisdom and financial growth)', 'Underground in SE (Fire & water clash)'],
    deityOrLord: 'Lord Varuna (Water God) & Lord Shiva',
    orientationGuidelines: 'Underground water belongs to Water element (NE/North). Overhead water represents Heavy Weight (Earth element / SW/West).',
    orientationGuidelinesHi: 'भूमिगत जल स्रोत उत्तर-पूर्व (ईशान) में होना चाहिए। छत पर रखी भारी पानी की टंकी दक्षिण-पश्चिम (नैऋत्य) में होनी चाहिए।',
    vastuRules: [
      'Never dig a borewell or place an underground water sump in the South-West or South-East.',
      'The overhead tank in South-West should be elevated on a platform, making SW the highest point of the roof.',
      'Ensure the overhead water tank does not leak or drip water continuously on the roof terrace (causes slow financial drain).'
    ],
    vastuRulesHi: [
      'नैऋत्य (SW) अथवा आग्नेय (SE) में कभी भी भूमिगत जल कुंड या बोरवेल न बनाएं।',
      'छत की टंकी को दक्षिण-पश्चिम में ईंटों के चबूतरे पर रखें ताकि वह छत का सबसे ऊंचा बिंदु बने।',
      'पानी की टंकी से पानी का रिसाव (Leakage) कभी न होने दें, इससे धन का अपव्यय होता है।'
    ],
    doshaEffects: 'Overhead tank in North-East causes severe migraine, strokes, mental breakdown, and business paralysis.',
    doshaEffectsHi: 'ईशान कोण में छत पर भारी टंकी होने से सिरदर्द, मानसिक तनाव और व्यापार में भारी रुकावटें आती हैं।',
    remediesWithoutDemolition: [
      'Paint the overhead tank in NE with bright yellow paint and place it on a wooden base if immovable.',
      'If underground tank is in SW, plant heavy potted ficus trees around it and place a lead helix on all corners.'
    ],
    remediesWithoutDemolitionHi: [
      'यदि छत की टंकी ईशान में हो तो उस पर पीला रंग करें और नीचे लकड़ी का बोर्ड लगाएं।',
      'यदि भूमिगत टंकी नैऋत्य में हो तो उसके चारों ओर भारी गमले रखें व शीशे/लेड का पिरामिड स्थापित करें।'
    ]
  }
];

// --- 16-ZONE COMPASS DEGREE RESOLVER ---
export function getZoneByDegree(degrees: number): VastuZone {
  const norm = ((degrees % 360) + 360) % 360;
  for (const zone of VASTU_16_ZONES) {
    const [min, max] = zone.range;
    if (min > max) {
      // True North sector wraparound (348.75° to 11.25°)
      if (norm >= min || norm < max) return zone;
    } else {
      if (norm >= min && norm < max) return zone;
    }
  }
  return VASTU_16_ZONES[0];
}

// --- DETERMINISTIC ROOM PLACEMENT EVALUATION & ZONE TOKEN MATCHING ---
const ZONE_TOKEN_PATTERNS: Record<string, RegExp> = {
  N: /(?<!(North|South|East|West|North\s*[-–]|South\s*[-–]|East\s*[-–]|West\s*[-–])\s*)\b(North|Uttar|N3|N4|N5)\b(?!\s*[-–]\s*(East|West|North|South))/i,
  NNE: /\b(NNE|North-North-East|North\s+North\s+East)\b/i,
  NE: /(?<!(East|North)\s*[-–]\s*)\b(NE|North-East|North\s+East|Ishanya|Ishana)\b/i,
  ENE: /\b(ENE|East-North-East|East\s+North\s+East)\b/i,
  E: /(?<!(North|South|East|West|North\s*[-–]|South\s*[-–]|East\s*[-–]|West\s*[-–])\s*)\b(East|Purva|E3|E4|E5)\b(?!\s*[-–]\s*(North|South|East|West))/i,
  ESE: /\b(ESE|East-South-East|East\s+South\s+East)\b/i,
  SE: /(?<!(East|South)\s*[-–]\s*)\b(SE|South-East|South\s+East|Agneya)\b/i,
  SSE: /\b(SSE|South-South-East|South\s+South\s+East)\b/i,
  S: /(?<!(North|South|East|West|North\s*[-–]|South\s*[-–]|East\s*[-–]|West\s*[-–])\s*)\b(South|Dakshin|S1|S2|S3|S4|S5)\b(?!\s*[-–]\s*(East|West|North|South))/i,
  SSW: /\b(SSW|South-South-West|South\s+South\s+West)\b/i,
  SW: /(?<!(West|South)\s*[-–]\s*)\b(SW|South-West|South\s+West|Nairutya|Nirriti)\b/i,
  WSW: /\b(WSW|West-South-West|West\s+South\s+West)\b/i,
  W: /(?<!(North|South|East|West|North\s*[-–]|South\s*[-–]|East\s*[-–]|West\s*[-–])\s*)\b(West|Paschim|W3|W4)\b(?!\s*[-–]\s*(North|South|East|West))/i,
  WNW: /\b(WNW|West-North-West|West\s+North\s+West)\b/i,
  NW: /(?<!(West|North)\s*[-–]\s*)\b(NW|North-West|North\s+West|Vayavya)\b/i,
  NNW: /\b(NNW|North-North-West|North\s+North\s+West)\b/i
};

export function matchesVastuZoneToken(zoneDescriptor: string, targetZoneId: string): boolean {
  if (!zoneDescriptor || !targetZoneId) return false;
  const desc = zoneDescriptor.trim();
  const id = targetZoneId.trim().toUpperCase();
  const pattern = ZONE_TOKEN_PATTERNS[id];
  if (!pattern) return false;
  const startsWithId = new RegExp(`^${id}(\\s|\\(|\\b)`, 'i').test(desc);
  return startsWithId || pattern.test(desc);
}

export interface VastuPlacementEvaluation {
  status: 'excellent' | 'moderate' | 'dosh';
  placement: SignificantPlacement;
  zone: VastuZone;
  rationale: string;
}

export function evaluateVastuPlacement(roomId: string, zoneId: string): VastuPlacementEvaluation | null {
  const placement = SIGNIFICANT_PLACEMENTS.find((p) => p.id === roomId);
  const zone = VASTU_16_ZONES.find((z) => z.id === zoneId);
  if (!placement || !zone) return null;

  const isBest = placement.bestZones.some((z) => matchesVastuZoneToken(z, zoneId));
  const isForbidden = placement.forbiddenZones.some((z) => matchesVastuZoneToken(z, zoneId));
  const isSecondary = placement.secondaryZones.some((z) => matchesVastuZoneToken(z, zoneId));

  let status: 'excellent' | 'moderate' | 'dosh' = 'moderate';
  let rationale = '';

  if (isBest) {
    status = 'excellent';
    rationale = `${placement.title} placed in ${zone.name} is highly auspicious and aligns with classical Vastu principles.`;
  } else if (isForbidden) {
    status = 'dosh';
    rationale = `Vastu Dosh Detected: ${placement.title} is strictly forbidden in ${zone.name}. ${placement.doshaEffects}`;
  } else if (isSecondary) {
    status = 'moderate';
    rationale = `${placement.title} in ${zone.name} is acceptable as a secondary placement with balanced spatial remedies.`;
  } else {
    status = 'moderate';
    rationale = `Neutral spatial placement. Ensure cleanliness and follow proper orientation guidelines.`;
  }

  return {
    status,
    placement,
    zone,
    rationale
  };
}

// Helper to correlate Kundali 12 Houses with Home Vastu Zones
export function calculateAstroVastuCorrelations(kundali?: CompleteKundali | null): AstroVastuCorrelation[] {
  if (!kundali) {
    // Complete 12-House Standard Baseline
    return [
      {
        houseNumber: 1,
        houseName: '1st House (Lagna / Self & Vitality)',
        houseNameHi: 'प्रथम भाव (लग्न / स्वयं व ओज)',
        direction: 'East (Purva)',
        directionHi: 'पूर्व दिशा',
        rulingPlanet: 'Sun & Mars',
        rulingPlanetHi: 'सूर्य व मंगल',
        significance: 'Physical vitality, immunity, public identity, and head.',
        significanceHi: 'शारीरिक ओज, स्वास्थ्य, मान-सम्मान व व्यक्तित्व।',
        kundaliObservation: 'Standard Alignment',
        vastuAdvice: 'Keep the East zone spacious, airy, with minimal clutter to empower personal authority.',
        status: 'balanced'
      },
      {
        houseNumber: 2,
        houseName: '2nd House (Dhana / Family & Wealth)',
        houseNameHi: 'द्वितीय भाव (धन एवं कुटुंब)',
        direction: 'North-East & North',
        directionHi: 'ईशान व उत्तर दिशा',
        rulingPlanet: 'Jupiter & Mercury',
        rulingPlanetHi: 'बृहस्पति व बुध',
        significance: 'Accumulated savings, family harmony, speech, and treasury.',
        significanceHi: 'संचित धन, पारिवारिक सुख, वाणी व तिजोरी।',
        kundaliObservation: 'Standard Alignment',
        vastuAdvice: 'Ensure the North-East and North zones have no toilets or red clutter to safeguard savings.',
        status: 'excellent'
      },
      {
        houseNumber: 3,
        houseName: '3rd House (Sahaja / Courage & Enterprise)',
        houseNameHi: 'तृतीय भाव (सहज / पराक्रम व बंधु)',
        direction: 'East-South-East (ESE) & South',
        directionHi: 'पूर्व-दक्षिण-पूर्व एवं दक्षिण',
        rulingPlanet: 'Mars (Mangala)',
        rulingPlanetHi: 'मंगल ग्रह',
        significance: 'Initiative, willpower, siblings, short travel, communication and marketing.',
        significanceHi: 'पराक्रम, आत्मबल, भाई-बहन, लघु यात्राएं व संचार।',
        kundaliObservation: 'Standard Alignment',
        vastuAdvice: 'Keep the ESE zone balanced; avoid sleeping here, but place work telephone or analytical books to boost drive.',
        status: 'balanced'
      },
      {
        houseNumber: 4,
        houseName: '4th House (Sukha / Home & Mother)',
        houseNameHi: 'चतुर्थ भाव (सुख, गृह एवं माता)',
        direction: 'North-East (NE) & North',
        directionHi: 'ईशान कोण व उत्तर',
        rulingPlanet: 'Moon (Chandra)',
        rulingPlanetHi: 'चंद्र ग्रह',
        significance: 'Domestic peace, emotional happiness, mother’s health, vehicles, and real estate comfort.',
        significanceHi: 'गृह शांति, माता का स्वास्थ्य, वाहन सुख व स्थायी संपत्ति।',
        kundaliObservation: 'Standard Alignment',
        vastuAdvice: 'Place a small urn of Gangajal or silver bowl in the North-East to nurture home serenity.',
        status: 'excellent'
      },
      {
        houseNumber: 5,
        houseName: '5th House (Putra / Intellect & Progeny)',
        houseNameHi: 'पंचम भाव (पुत्र / बुद्धि व ज्ञान)',
        direction: 'East-North-East (ENE) & East',
        directionHi: 'पूर्व-उत्तर-पूर्व एवं पूर्व',
        rulingPlanet: 'Jupiter & Sun',
        rulingPlanetHi: 'बृहस्पति व सूर्य',
        significance: 'Wisdom, children, higher learning, speculative intuition, and creative joy.',
        significanceHi: 'ज्ञान, संतान सुख, उच्च शिक्षा, रचनात्मकता व विवेक।',
        kundaliObservation: 'Standard Alignment',
        vastuAdvice: 'Keep ENE clear and airy; hang family portraits or place books on philosophy to cultivate joyful wisdom.',
        status: 'excellent'
      },
      {
        houseNumber: 6,
        houseName: '6th House (Ari / Health & Competition)',
        houseNameHi: 'षष्ठ भाव (अरि / रोग, ऋण व शत्रु)',
        direction: 'South-South-West (SSW) & North-West',
        directionHi: 'दक्षिण-दक्षिण-पश्चिम एवं वायव्य',
        rulingPlanet: 'Mars & Saturn',
        rulingPlanetHi: 'मंगल व शनि',
        significance: 'Immunity against disease, debt elimination, competitive edge, and workplace service.',
        significanceHi: 'रोग प्रतिरोध, ऋण मुक्ति, शत्रु दमन व कार्यस्थल समन्वय।',
        kundaliObservation: 'Standard Alignment',
        vastuAdvice: 'Use SSW strictly for disposal, dustbin or toilet; ensure no bed or locker is placed here to avoid draining stamina.',
        status: 'balanced'
      },
      {
        houseNumber: 7,
        houseName: '7th House (Kalatra / Marriage & Business)',
        houseNameHi: 'सप्तम भाव (कलत्र / दांपत्य व साझेदारी)',
        direction: 'South-West (SW) & West',
        directionHi: 'नैऋत्य एवं पश्चिम दिशा',
        rulingPlanet: 'Venus (Shukra)',
        rulingPlanetHi: 'शुक्र ग्रह',
        significance: 'Marital bonding, romantic chemistry, business contracts, and client relations.',
        significanceHi: 'दांपत्य प्रेम, व्यापारिक साझेदारी, ग्राहक संबंध व स्थायित्व।',
        kundaliObservation: 'Standard Alignment',
        vastuAdvice: 'Ensure master bedroom is in South-West with head towards South. Use warm pastel colors.',
        status: 'excellent'
      },
      {
        houseNumber: 8,
        houseName: '8th House (Randhra / Longevity & Transformation)',
        houseNameHi: 'अष्टम भाव (रंध्र / आयु व गूढ़ ज्ञान)',
        direction: 'South (Dakshin / Yama Zone)',
        directionHi: 'दक्षिण दिशा (यम स्थान)',
        rulingPlanet: 'Saturn & Mars',
        rulingPlanetHi: 'शनि व मंगल',
        significance: 'Longevity, insurance, inheritance, research, and unexpected transformational events.',
        significanceHi: 'आयु, गुप्त विद्याएं, पैतृक संपत्ति व आकस्मिक लाभ।',
        kundaliObservation: 'Standard Alignment',
        vastuAdvice: 'Keep South heavy and well-sealed. Avoid underground borewells or entrances in South S1/S2 to shield against setbacks.',
        status: 'balanced'
      },
      {
        houseNumber: 9,
        houseName: '9th House (Dharma / Higher Fortune & Grace)',
        houseNameHi: 'नवम भाव (धर्म / भाग्य व गुरु कृपा)',
        direction: 'North-East (Ishanya)',
        directionHi: 'ईशान कोण (देव स्थान)',
        rulingPlanet: 'Jupiter & Sun',
        rulingPlanetHi: 'बृहस्पति व सूर्य',
        significance: 'Spiritual evolution, mentor support, legal justice, foreign pilgrimages, and fatherly blessings.',
        significanceHi: 'धर्म, गुरु का मार्गदर्शन, न्याय, तीर्थ यात्राएं व उच्च भाग्य।',
        kundaliObservation: 'Standard Alignment',
        vastuAdvice: 'Keep the North-East pristine, free from heavy clutter or toilets; maintain an illuminated spiritual altar here.',
        status: 'excellent'
      },
      {
        houseNumber: 10,
        houseName: '10th House (Karma / Career & Status)',
        houseNameHi: 'दशम भाव (कर्म, पद व प्रतिष्ठा)',
        direction: 'South & North',
        directionHi: 'दक्षिण एवं उत्तर दिशा',
        rulingPlanet: 'Saturn, Sun, Mars',
        rulingPlanetHi: 'शनि, सूर्य, मंगल',
        significance: 'Career trajectory, fame, promotions, business expansion, and public reputation.',
        significanceHi: 'आजीविका, पदोन्नति, व्यापार विस्तार व कीर्ति।',
        kundaliObservation: 'Standard Alignment',
        vastuAdvice: 'Hang awards or metallic logo on South/North wall to amplify career velocity.',
        status: 'excellent'
      },
      {
        houseNumber: 11,
        houseName: '11th House (Labha / Profits & Desires)',
        houseNameHi: 'एकादश भाव (लाभ व इच्छा पूर्ति)',
        direction: 'West (Paschim)',
        directionHi: 'पश्चिम दिशा',
        rulingPlanet: 'Saturn & Jupiter',
        rulingPlanetHi: 'शनि व गुरु',
        significance: 'Realized gains, high profit margins, influential social circle, and wish manifestation.',
        significanceHi: 'व्यापारिक लाभ, बड़े भाई-बहन, मित्र वर्ग व अभिलाषा पूर्ति।',
        kundaliObservation: 'Standard Alignment',
        vastuAdvice: 'Place a round metal table or dining area in the West to compound revenues.',
        status: 'excellent'
      },
      {
        houseNumber: 12,
        houseName: '12th House (Vyaya / Expenditure & Moksha)',
        houseNameHi: 'द्वादश भाव (व्यय / मोक्ष व विदेश)',
        direction: 'North-West (NW) & South-South-West (SSW)',
        directionHi: 'वायव्य एवं SSW दिशा',
        rulingPlanet: 'Rahu & Ketu',
        rulingPlanetHi: 'राहु व केतु',
        significance: 'Controlled expenditures, sound sleep, foreign settlement, and spiritual liberation.',
        significanceHi: 'व्यय नियंत्रण, गहरी नींद, विदेश प्रवास व मोक्ष साधना।',
        kundaliObservation: 'Standard Alignment',
        vastuAdvice: 'Guest bedroom or finished goods storage in NW promotes healthy rotation; avoid keeping cash locker here.',
        status: 'balanced'
      }
    ];
  }

  // Dynamic calculation based on user Kundali
  const planets = kundali.planets || [];
  const houses = kundali.houses || [];

  const sun = planets.find((p) => p.name === 'Sun');
  const moon = planets.find((p) => p.name === 'Moon');
  const mars = planets.find((p) => p.name === 'Mars');
  const mercury = planets.find((p) => p.name === 'Mercury');
  const jupiter = planets.find((p) => p.name === 'Jupiter');
  const venus = planets.find((p) => p.name === 'Venus');
  const saturn = planets.find((p) => p.name === 'Saturn');
  const rahu = planets.find((p) => p.name === 'Rahu');

  const ascSign = houses[0]?.sign || 'Aries';

  const results: AstroVastuCorrelation[] = [
    {
      houseNumber: 1,
      houseName: `1st House (${ascSign} Lagna - Self & Vitality)`,
      houseNameHi: `प्रथम भाव (${ascSign} लग्न - स्वास्थ्य व ओज)`,
      direction: 'East (Purva)',
      directionHi: 'पूर्व दिशा',
      rulingPlanet: sun ? `Sun in ${sun.sign} (H${sun.house})` : 'Sun',
      rulingPlanetHi: sun ? `सूर्य (${sun.sign}, भाव ${sun.house})` : 'सूर्य',
      significance: 'Physical vitality, immunity, clarity of life direction, and public reputation.',
      significanceHi: 'शारीरिक स्वास्थ्य, रोग प्रतिरोधक क्षमता, जीवन की दिशा व यश।',
      kundaliObservation: sun?.sign === 'Libra' ? 'Sun is in Libra (Debilitated)' : (sun?.sign === 'Aries' ? 'Sun is Exalted in Aries' : 'Lagna Vitality Active'),
      vastuAdvice: sun?.sign === 'Libra'
        ? 'Hang a Copper Surya on the East wall and maintain bright lighting in the East zone to strengthen lagna vitality.'
        : (sun?.sign === 'Aries'
            ? 'East wall is exceptionally empowered. Keep clear windows to invite dawn prana and amplify leadership.'
            : 'Keep the East wall light and airy; place green plants to support social connectivity.'),
      status: sun?.sign === 'Libra' ? 'attention_needed' : 'excellent'
    },
    {
      houseNumber: 2,
      houseName: `2nd House (${houses[1]?.sign || 'Taurus'} - Wealth & Family Harmony)`,
      houseNameHi: `द्वितीय भाव (${houses[1]?.sign || 'वृषभ'} - धन संचय व कुटुंब)`,
      direction: 'North (Kubera Zone)',
      directionHi: 'उत्तर (कुबेर दिशा)',
      rulingPlanet: mercury ? `Mercury in ${mercury.sign}` : 'Mercury & Jupiter',
      rulingPlanetHi: mercury ? `बुध (${mercury.sign})` : 'बुध व गुरु',
      significance: 'Treasury, liquid cash, bank balance, voice, and family unity.',
      significanceHi: 'बैंक बैलेंस, तिजोरी, परिवार में मधुरता व धन वृद्धि।',
      kundaliObservation: mercury?.isRetrograde ? 'Mercury is Retrograde' : 'Wealth Flow Active',
      vastuAdvice: mercury?.isRetrograde
        ? 'Place a Kubera Yantra and an emerald green plant in North to organize fluctuating liquidity and speech.'
        : 'Place a clean Green Money plant in a blue glass bottle in the North zone to amplify wealth inflow.',
      status: mercury?.isRetrograde ? 'balanced' : 'excellent'
    },
    {
      houseNumber: 3,
      houseName: `3rd House (${houses[2]?.sign || 'Gemini'} - Courage, Siblings & Drive)`,
      houseNameHi: `तृतीय भाव (${houses[2]?.sign || 'मिथुन'} - पराक्रम, भाई-बहन व उद्यम)`,
      direction: 'East-South-East (ESE) & South',
      directionHi: 'पूर्व-दक्षिण-पूर्व एवं दक्षिण',
      rulingPlanet: mars ? `Mars in ${mars.sign} (H${mars.house})` : 'Mars',
      rulingPlanetHi: mars ? `मंगल (${mars.sign}, भाव ${mars.house})` : 'मंगल देव',
      significance: 'Initiative, short business journeys, technical hands-on skill, and resolute willpower.',
      significanceHi: 'उद्यमशीलता, लघु व्यावसायिक यात्राएं, तकनीकी कौशल व इच्छाशक्ति।',
      kundaliObservation: mars?.sign === 'Cancer' ? 'Mars is in Cancer (Debilitated)' : 'Dynamic Enterprise Active',
      vastuAdvice: mars?.sign === 'Cancer'
        ? 'Place a solid brass Kamdhenu Cow in ESE and keep the South zone grounded with warm terracotta to steady impulsive anxiety.'
        : 'Keep the ESE zone decluttered; position work computer or study materials facing East.',
      status: mars?.sign === 'Cancer' ? 'attention_needed' : 'balanced'
    },
    {
      houseNumber: 4,
      houseName: `4th House (${houses[3]?.sign || 'Cancer'} - Home Serenity, Mother & Assets)`,
      houseNameHi: `चतुर्थ भाव (${houses[3]?.sign || 'कर्क'} - गृह शांति, माता व संपत्ति)`,
      direction: 'North-East (NE) & North',
      directionHi: 'ईशान कोण व उत्तर',
      rulingPlanet: moon ? `Moon in ${moon.sign} (H${moon.house})` : 'Moon',
      rulingPlanetHi: moon ? `चंद्र (${moon.sign}, भाव ${moon.house})` : 'चंद्र देव',
      significance: 'Domestic contentment, vehicles, maternal health, and mental calm.',
      significanceHi: 'मानसिक शांति, घर का सुख, वाहन व माता का स्वास्थ्य।',
      kundaliObservation: moon?.sign === 'Scorpio' ? 'Moon in Scorpio (Sensitive mind)' : (moon?.sign === 'Taurus' ? 'Moon is Exalted in Taurus' : 'Home House Active'),
      vastuAdvice: moon?.sign === 'Scorpio'
        ? 'Place a silver bowl with clean water and white flowers in the North-East to soothe mental anxieties.'
        : (moon?.sign === 'Taurus'
            ? 'Home sanctuary is exceptionally blessed. Maintain pristine crystal or marble mandir in North-East for domestic bliss.'
            : 'Keep North-East zone sparkling clean and place the home mandir here.'),
      status: moon?.sign === 'Scorpio' ? 'attention_needed' : 'excellent'
    },
    {
      houseNumber: 5,
      houseName: `5th House (${houses[4]?.sign || 'Leo'} - Intellect, Progeny & Wisdom)`,
      houseNameHi: `पंचम भाव (${houses[4]?.sign || 'सिंह'} - बुद्धि, संतान व विवेक)`,
      direction: 'East-North-East (ENE) & East',
      directionHi: 'पूर्व-उत्तर-पूर्व एवं पूर्व',
      rulingPlanet: jupiter ? `Jupiter in ${jupiter.sign} (H${jupiter.house})` : 'Jupiter & Sun',
      rulingPlanetHi: jupiter ? `गुरु (${jupiter.sign}, भाव ${jupiter.house})` : 'बृहस्पति व सूर्य',
      significance: 'Academic merit, intuitive creativity, speculative acumen, and children’s prosperity.',
      significanceHi: 'ज्ञान, संतान सुख, उच्च शिक्षा, रचनात्मकता व विवेक।',
      kundaliObservation: jupiter?.sign === 'Capricorn' ? 'Jupiter is in Capricorn (Debilitated)' : 'Intellect & Wisdom Radiant',
      vastuAdvice: jupiter?.sign === 'Capricorn'
        ? 'Place yellow brass flowers or a wooden Saraswati idol in ENE to revitalize intellectual focus and academic confidence.'
        : 'Display educational diplomas, vibrant family portraits, or indoor plants in ENE to foster joy and learning.',
      status: jupiter?.sign === 'Capricorn' ? 'attention_needed' : 'excellent'
    },
    {
      houseNumber: 6,
      houseName: `6th House (${houses[5]?.sign || 'Virgo'} - Health, Service & Competition)`,
      houseNameHi: `षष्ठ भाव (${houses[5]?.sign || 'कन्या'} - स्वास्थ्य, सेवा व प्रतिस्पर्धा)`,
      direction: 'South-South-West (SSW) & North-West',
      directionHi: 'दक्षिण-दक्षिण-पश्चिम एवं वायव्य',
      rulingPlanet: mars ? `Mars/Saturn (${mars.sign})` : 'Mars & Saturn',
      rulingPlanetHi: mars ? `मंगल/शनि (${mars.sign})` : 'मंगल व शनि',
      significance: 'Defeating competition, overcoming illnesses, disciplined service, and resolving debts.',
      significanceHi: 'प्रतिस्पर्धा में विजय, रोग निवारण, नियमित सेवा व ऋण मुक्ति।',
      kundaliObservation: 'Competitive Strength Evaluated',
      vastuAdvice: 'Strictly designate SSW for disposal/toilet or storage; avoid sleeping in SSW to prevent exhaustion of physical immunity.',
      status: 'balanced'
    },
    {
      houseNumber: 7,
      houseName: `7th House (${houses[6]?.sign || 'Libra'} - Marriage, Partnership & Business)`,
      houseNameHi: `सप्तम भाव (${houses[6]?.sign || 'तुला'} - विवाह, दांपत्य व साझेदारी)`,
      direction: 'South-West (SW) & North-North-West (NNW)',
      directionHi: 'नैऋत्य एवं NNW दिशा',
      rulingPlanet: venus ? `Venus in ${venus.sign} (H${venus.house})` : 'Venus',
      rulingPlanetHi: venus ? `शुक्र (${venus.sign}, भाव ${venus.house})` : 'शुक्र देव',
      significance: 'Marital bonding, romantic chemistry, business contracts, and client relations.',
      significanceHi: 'दांपत्य प्रेम, व्यापारिक साझेदारी, ग्राहक संबंध व स्थायित्व।',
      kundaliObservation: venus?.isCombust ? 'Venus is Combust' : (venus?.sign === 'Virgo' ? 'Venus in Virgo (Debilitated)' : 'Partnership Active'),
      vastuAdvice: (venus?.isCombust || venus?.sign === 'Virgo')
        ? 'Place rose quartz stones and soft cream/pastel decor in South-West. Ensure no blue/water elements reside in SW.'
        : 'Ensure master bedroom is in South-West with head towards South. Use warm pastel colors.',
      status: (venus?.isCombust || venus?.sign === 'Virgo') ? 'attention_needed' : 'excellent'
    },
    {
      houseNumber: 8,
      houseName: `8th House (${houses[7]?.sign || 'Scorpio'} - Longevity & Hidden Transformation)`,
      houseNameHi: `अष्टम भाव (${houses[7]?.sign || 'वृश्चिक'} - आयु, गूढ़ विद्या व रूपांतरण)`,
      direction: 'South (Dakshin / Yama Zone)',
      directionHi: 'दक्षिण दिशा (यम स्थान)',
      rulingPlanet: saturn ? `Saturn in ${saturn.sign} (H${saturn.house})` : 'Saturn & Mars',
      rulingPlanetHi: saturn ? `शनि (${saturn.sign}, भाव ${saturn.house})` : 'शनि व मंगल',
      significance: 'Long life, unearned wealth, deep research, esoteric wisdom, and resilience against sudden crisis.',
      significanceHi: 'दीर्घायु, अनुसंधान, आकस्मिक धन व संकट से मुक्ति।',
      kundaliObservation: saturn?.sign === 'Aries' ? 'Saturn in Aries (Debilitated)' : 'Longevity & Transformation Stable',
      vastuAdvice: saturn?.sign === 'Aries'
        ? 'Reinforce the South zone with solid heavy wooden furniture or clay planters; avoid light glass facades on the South wall.'
        : 'Keep South wall elevated and heavy; place heavy cupboards or storage to create solid protective stability.',
      status: saturn?.sign === 'Aries' ? 'attention_needed' : 'balanced'
    },
    {
      houseNumber: 9,
      houseName: `9th House (${houses[8]?.sign || 'Sagittarius'} - Dharma, Wisdom & Fortune)`,
      houseNameHi: `नवम भाव (${houses[8]?.sign || 'धनु'} - धर्म, भाग्य व गुरु कृपा)`,
      direction: 'North-East (Ishanya)',
      directionHi: 'ईशान कोण (देव स्थान)',
      rulingPlanet: jupiter ? `Jupiter in ${jupiter.sign} (H${jupiter.house})` : 'Jupiter & Sun',
      rulingPlanetHi: jupiter ? `गुरु (${jupiter.sign}, भाव ${jupiter.house})` : 'बृहस्पति व सूर्य',
      significance: 'Spiritual evolution, mentor support, legal justice, foreign pilgrimages, and fatherly blessings.',
      significanceHi: 'धर्म, गुरु का मार्गदर्शन, न्याय, तीर्थ यात्राएं व उच्च भाग्य।',
      kundaliObservation: 'Dharma & Fortune Channel Active',
      vastuAdvice: 'Keep North-East zone pristine and lightweight; avoid red or dark charcoal tones here to maintain unobstructed spiritual grace.',
      status: 'excellent'
    },
    {
      houseNumber: 10,
      houseName: `10th House (${houses[9]?.sign || 'Capricorn'} - Career, Authority & Karma)`,
      houseNameHi: `दशम भाव (${houses[9]?.sign || 'मकर'} - आजीविका, पद व कर्म)`,
      direction: 'South (Fame) & North (Opportunities)',
      directionHi: 'दक्षिण (यश) व उत्तर (अवसर)',
      rulingPlanet: saturn ? `Saturn in ${saturn.sign} (H${saturn.house})` : 'Saturn & Sun',
      rulingPlanetHi: saturn ? `शनि (${saturn.sign}, भाव ${saturn.house})` : 'शनि व सूर्य',
      significance: 'Professional promotions, enterprise leadership, government recognition, and achievements.',
      significanceHi: 'पदोन्नति, व्यावसायिक सफलता, सरकारी मान-सम्मान व कीर्ति।',
      kundaliObservation: 'Career Houses Synchronized',
      vastuAdvice: 'Sit facing North while working. Keep certificates framed on the South wall to boost public fame.',
      status: 'excellent'
    },
    {
      houseNumber: 11,
      houseName: `11th House (${houses[10]?.sign || 'Aquarius'} - Gains, Revenue & Desires)`,
      houseNameHi: `एकादश भाव (${houses[10]?.sign || 'कुंभ'} - लाभ, आय व इच्छा पूर्ति)`,
      direction: 'West (Paschim)',
      directionHi: 'पश्चिम दिशा',
      rulingPlanet: jupiter ? `Jupiter in ${jupiter.sign}` : 'Jupiter & Saturn',
      rulingPlanetHi: jupiter ? `गुरु (${jupiter.sign})` : 'बृहस्पति व शनि',
      significance: 'Revenue compounding, profit realization, investor networks, and fulfilled goals.',
      significanceHi: 'व्यापारिक लाभ, मुनाफा, बड़े नेटवर्क से सहयोग व मनोरथ सिद्धि।',
      kundaliObservation: 'Labha House Analyzed',
      vastuAdvice: 'Place dining table or accounts desk in the West zone. Keep heavy overhead tank in West.',
      status: 'excellent'
    },
    {
      houseNumber: 12,
      houseName: `12th House (${houses[11]?.sign || 'Pisces'} - Expenditure, Foreign Ties & Moksha)`,
      houseNameHi: `द्वादश भाव (${houses[11]?.sign || 'मीन'} - व्यय, विदेश व मोक्ष)`,
      direction: 'North-West (NW) & South-South-West (SSW)',
      directionHi: 'वायव्य एवं SSW दिशा',
      rulingPlanet: rahu ? `Rahu/Ketu (${rahu.sign})` : 'Rahu & Ketu',
      rulingPlanetHi: rahu ? `राहु/केतु (${rahu.sign})` : 'राहु व केतु',
      significance: 'Peaceful sleep, elimination of wasteful expenses, international trade, and spiritual detachment.',
      significanceHi: 'गहरी नींद, व्यय पर नियंत्रण, अंतरराष्ट्रीय संबंध व मोक्ष साधना।',
      kundaliObservation: 'Expenditure & Rest House Analyzed',
      vastuAdvice: 'Keep the NW zone active with good air circulation; avoid keeping important bank savings books or lockers in NW.',
      status: 'balanced'
    }
  ];

  return results;
}
